import {
  Box,
  Modal,
  Tabs,
} from '@bigcommerce/big-design';
import { useState } from 'react';

import { BigCommerceInfoTab } from './BigCommerceInfoTab';
import { GeneralSettingsTab } from './GeneralSettingsTab';
import { PaymentsSettingsTab } from './PaymentsSettingsTab';
import { ReceiptsSettingsTab } from './ReceiptsSettingsTab';
import type { Channel, Currency, Location, LocationFormData, PickupMethod, TaxZone } from './types';
import { DEFAULT_PAYMENT_METHODS, DEFAULT_TIP_CONFIG } from './types';

interface LocationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: Location | null;
  currencies: Currency[];
  taxZones: TaxZone[];
  channels: Channel[];
  pickupMethods: PickupMethod[];
  storeChannelId: number;
  defaultCurrency: string;
  defaultPickupMethodId: number | null;
  isSaving: boolean;
  onSave: (location: Location, formData: LocationFormData) => Promise<void>;
}

export function LocationEditModal({
  isOpen,
  onClose,
  location,
  currencies,
  taxZones,
  channels,
  pickupMethods,
  storeChannelId,
  defaultCurrency,
  defaultPickupMethodId,
  isSaving,
  onSave,
}: LocationEditModalProps) {
  const [modalTab, setModalTab] = useState('general');

  const getInitialFormData = (): LocationFormData => {
    const settings = location?.posSettings;
    return {
      displayName: settings?.displayName || location?.label || '',
      currency: settings?.currency || defaultCurrency,
      taxZoneId: settings?.taxZoneId || null,
      bcChannelId: settings?.bcChannelId || null, // null = use store default
      pickupMethodId: settings?.pickupMethodId || defaultPickupMethodId,
      defaultReceiptMethod: settings?.defaultReceiptMethod || 'ask',
      requireCustomerEmail: settings?.requireCustomerEmail || false,
      requireCustomerPhone: settings?.requireCustomerPhone || false,
      paymentMethods: settings?.paymentMethods || { ...DEFAULT_PAYMENT_METHODS },
      tip: settings?.tip || { ...DEFAULT_TIP_CONFIG },
      receiptOverride: settings?.receiptOverride || undefined,
    };
  };

  const [formData, setFormData] = useState<LocationFormData>(getInitialFormData);

  // Reset form when location changes
  const handleOpen = () => {
    setFormData(getInitialFormData());
    setModalTab('general');
  };

  // Handle open state changes
  if (isOpen && formData.displayName === '' && location?.label) {
    handleOpen();
  }

  const handleChange = (updates: Partial<LocationFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    if (!location) return;
    await onSave(location, formData);
    onClose();
  };

  const handleClose = () => {
    setModalTab('general');
    onClose();
  };

  const isEdit = location?.isConfiguredForPOS ?? false;
  const storeName = location?.posSettings?.displayName || location?.label || 'Store';

  // Check if essential settings are configured
  const hasPickupMethod = !!formData.pickupMethodId;
  const hasCurrency = !!formData.currency;
  const isStartHereComplete = hasPickupMethod && hasCurrency;

  // Build descriptive header
  const modalHeader = isEdit
    ? `Edit Store: ${storeName}`
    : `Set Up Store: ${location?.label || 'Location'}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header={modalHeader}
      variant="dialog"
      actions={[
        {
          text: 'Cancel',
          variant: 'subtle',
          onClick: handleClose,
        },
        {
          text: isEdit ? 'Save Changes' : 'Set Up Store',
          onClick: handleSave,
          isLoading: isSaving,
        },
      ]}
    >
      <Tabs
        activeTab={modalTab}
        items={[
          { id: 'general', title: isStartHereComplete ? 'Start Here' : 'Start Here ⚠️' },
          { id: 'receipts', title: 'Receipts' },
          { id: 'payments', title: 'Payments' },
          { id: 'bigcommerce', title: 'BigCommerce' },
        ]}
        onTabClick={setModalTab}
      />

      <Box
        marginTop="medium"
        style={{
          maxHeight: '60vh',
          overflowY: 'auto',
          paddingRight: '8px',
        }}
      >
        {modalTab === 'general' && (
          <GeneralSettingsTab
            formData={formData}
            onChange={handleChange}
            currencies={currencies}
            taxZones={taxZones}
            channels={channels}
            pickupMethods={pickupMethods}
            storeChannelId={storeChannelId}
            locationLabel={location?.label}
            locationId={location?.id}
          />
        )}

        {modalTab === 'receipts' && (
          <ReceiptsSettingsTab
            formData={formData}
            onChange={handleChange}
          />
        )}

        {modalTab === 'payments' && (
          <PaymentsSettingsTab
            formData={formData}
            onChange={handleChange}
          />
        )}

        {modalTab === 'bigcommerce' && location && (
          <BigCommerceInfoTab location={location} />
        )}
      </Box>
    </Modal>
  );
}
